/**
 * Panel Explorateur de fichiers — console opérateur (Solid.js).
 *
 * Explorateur DOUBLE-PANE style Windows :
 *   - GAUCHE  = LOCAL  (machine opérateur via File System Access API)
 *   - DROITE  = HOST   (via tools file_drives / file_list / file_search)
 *   - Drag inter-pane = transfert : LOCAL→HOST = upload, HOST→LOCAL = download
 *   - Barre de transferts en bas : progression, débit, pause/reprise/annuler,
 *     badge intégrité sha256, REPRISE automatique après reboot/reload.
 *
 * Protocole de fil (cf. .claude/output/file-transfer-protocol.md) :
 *   - Cadre binaire `FB` (magic 0x46 0x42) via ws.sendBinary(ArrayBuffer) — pas
 *     de base64. Header 18 octets + fileId, big-endian.
 *   - Contrôle JSON `file:*` (offer/resume/ack/done/complete/cancel/error) +
 *     `file:request` (download host→opérateur) + `file:pending` (reprise host).
 *   - Reprise par offset, sha256 streaming (WebCrypto), fenêtre 4 MiB.
 *
 * REPRISE (P3) : queue + FileSystemHandle persistés en IndexedDB. Au mount on
 * restaure, on re-demande la permission sur les handles et on auto-reprend les
 * transferts incomplets (offer pour upload / request pour download, même fileId).
 *
 * INVARIANT vidéo/RDP : ws.onBinary est partagé. Le handler IGNORE (return)
 * toute trame dont le magic n'est pas `FB`, pour ne pas perturber VB/MB/RDP.
 */
import {
  For,
  Show,
  createEffect,
  createMemo,
  createSignal,
  onCleanup,
  onMount,
  type Component,
  type JSX,
} from "solid-js";
import { PanelLayout } from "@/shared/panel-base";
import { useWs } from "@/shared/ws";
import { useToolClient } from "@/shared/tool-client";
import { registerPanel } from "@/panel-registry";
import "./files.css";

// ── Constantes protocole (cf. spec + legacy client.js) ───────────────────────
const FB_MAGIC0 = 0x46; // 'F'
const FB_MAGIC1 = 0x42; // 'B'
const FB_VERSION = 1;
const FB_FLAG_LAST = 0x01;
const FB_HEADER_FIXED = 18; // octets fixes avant fileId

const CHUNK_SIZE = 256 * 1024; // 256 KiB (spec §1)
const WINDOW_BYTES = 4 * 1024 * 1024; // fenêtre flow-control 4 MiB (spec §3)
const WS_BUFFER_HIGH_WATER = 4 * 1024 * 1024; // backpressure bufferedAmount
const ACK_INTERVAL_BYTES = 1024 * 1024; // ack récepteur tous les ~1 MiB (spec §4)
const FILE_MAX_BYTES = 100 * 1024 * 1024; // quota 100 MiB (spec §9)
const SEARCH_MAX_RESULTS = 500;

// MIME allowlist + sanitize — portés depuis client.js.
const ALLOWED_MIME_REGEX =
  /^(text\/|image\/|application\/(pdf|json|zip|x-tar|gzip|octet-stream|vnd\.openxmlformats)|video\/|audio\/)/i;

function isAllowedMime(mime: string): boolean {
  return ALLOWED_MIME_REGEX.test(mime || "application/octet-stream");
}

function sanitizeFilename(name: string): string | null {
  if (!name) return "fichier";
  if (/[\\/\x00-\x1f]/.test(name)) return null;
  return name.slice(0, 255);
}

function safeUuid(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  const b = new Uint8Array(16);
  crypto.getRandomValues(b);
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  return [...b]
    .map((x, i) => (i === 4 || i === 6 || i === 8 || i === 10 ? "-" : "") + x.toString(16).padStart(2, "0"))
    .join("");
}

function fmtBytes(n: number | null | undefined): string {
  if (n == null) return "—";
  if (n < 1024) return `${n} o`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} Ko`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} Mo`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} Go`;
}

function fmtRate(bytesPerSec: number): string {
  if (!Number.isFinite(bytesPerSec) || bytesPerSec <= 0) return "—";
  return `${fmtBytes(bytesPerSec)}/s`;
}

function fmtDate(s: string | null | undefined): string {
  if (!s) return "—";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" });
}

function toHex(buf: ArrayBuffer): string {
  return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

function extOf(name: string): string {
  const i = name.lastIndexOf(".");
  return i > 0 ? name.slice(i + 1).toLowerCase() : "";
}

// Icône par extension (emoji simple, cohérent thème sombre).
function fileIcon(name: string): string {
  const e = extOf(name);
  if (["png", "jpg", "jpeg", "gif", "bmp", "webp", "svg", "ico"].includes(e)) return "🖼";
  if (["mp4", "mkv", "avi", "mov", "wmv", "webm"].includes(e)) return "🎬";
  if (["mp3", "wav", "flac", "ogg", "m4a"].includes(e)) return "🎵";
  if (["zip", "rar", "7z", "tar", "gz"].includes(e)) return "🗜";
  if (["pdf"].includes(e)) return "📕";
  if (["doc", "docx", "rtf", "odt"].includes(e)) return "📘";
  if (["xls", "xlsx", "csv", "ods"].includes(e)) return "📗";
  if (["ppt", "pptx"].includes(e)) return "📙";
  if (["txt", "md", "log", "json", "xml", "yaml", "yml", "ini", "cfg"].includes(e)) return "📄";
  if (["exe", "msi", "dll", "bat", "cmd", "ps1", "sh"].includes(e)) return "⚙";
  return "📄";
}

// ── Construction du cadre FB (spec §1, big-endian) ───────────────────────────
function buildFbFrame(fileId: string, offset: number, payload: Uint8Array, last: boolean): ArrayBuffer {
  const idBytes = new TextEncoder().encode(fileId);
  const buf = new ArrayBuffer(FB_HEADER_FIXED + idBytes.length + payload.length);
  const view = new DataView(buf);
  const u8 = new Uint8Array(buf);
  view.setUint8(0, FB_MAGIC0);
  view.setUint8(1, FB_MAGIC1);
  view.setUint8(2, FB_VERSION);
  view.setUint8(3, last ? FB_FLAG_LAST : 0);
  view.setUint16(4, idBytes.length, false); // BE
  view.setBigUint64(6, BigInt(offset), false); // BE
  view.setUint32(14, payload.length, false); // BE
  u8.set(idBytes, FB_HEADER_FIXED);
  u8.set(payload, FB_HEADER_FIXED + idBytes.length);
  return buf;
}

interface ParsedFb {
  version: number;
  last: boolean;
  offset: number;
  fileId: string;
  payload: Uint8Array;
}

function parseFbFrame(data: ArrayBuffer): ParsedFb | null {
  if (data.byteLength < FB_HEADER_FIXED) return null;
  const view = new DataView(data);
  // Garde-fou vidéo/RDP : ne traiter que les trames FB, ignorer le reste.
  if (view.getUint8(0) !== FB_MAGIC0 || view.getUint8(1) !== FB_MAGIC1) return null;
  const version = view.getUint8(2);
  const flags = view.getUint8(3);
  const idLen = view.getUint16(4, false);
  const offset = Number(view.getBigUint64(6, false));
  const payloadLen = view.getUint32(14, false);
  const idStart = FB_HEADER_FIXED;
  const payloadStart = idStart + idLen;
  if (data.byteLength < payloadStart + payloadLen) return null;
  const fileId = new TextDecoder().decode(new Uint8Array(data, idStart, idLen));
  const payload = new Uint8Array(data, payloadStart, payloadLen);
  return { version, last: (flags & FB_FLAG_LAST) !== 0, offset, fileId, payload };
}

async function computeSha256Streaming(file: File): Promise<string> {
  // WebCrypto n'expose pas d'API update ; on lit le buffer (borné par le quota
  // FILE_MAX_BYTES = 100 MiB) et on digère en une passe.
  const buf = await file.arrayBuffer();
  const digest = await crypto.subtle.digest("SHA-256", buf);
  return toHex(digest);
}

// ════════════════════════════════════════════════════════════════════════════
// IndexedDB — persistance queue de transferts + FileSystemHandle (P3 reprise)
// ════════════════════════════════════════════════════════════════════════════
const IDB_NAME = "rws-files";
const IDB_VERSION = 1;
const STORE_QUEUE = "queue"; // métadonnées transferts (keyPath fileId)
const STORE_HANDLES = "handles"; // FileSystemHandle (structured-clone, keyPath fileId)

// Enregistrement persistant d'un transfert (commun upload/download).
interface PersistedTx {
  fileId: string;
  direction: "upload" | "download";
  name: string;
  size: number;
  mime: string;
  sha256: string | null;
  acked: number; // offset contigu confirmé (reprise)
  status: TxStatus;
  hostPath?: string; // chemin host (dossier ou fichier) impliqué
  updatedAt: number;
}

let idbPromise: Promise<IDBDatabase | null> | null = null;
function openIdb(): Promise<IDBDatabase | null> {
  if (idbPromise) return idbPromise;
  idbPromise = new Promise((resolve) => {
    try {
      if (typeof indexedDB === "undefined") return resolve(null);
      const req = indexedDB.open(IDB_NAME, IDB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(STORE_QUEUE)) db.createObjectStore(STORE_QUEUE, { keyPath: "fileId" });
        if (!db.objectStoreNames.contains(STORE_HANDLES)) db.createObjectStore(STORE_HANDLES, { keyPath: "fileId" });
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
  return idbPromise;
}

function idbReq<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function idbPutTx(tx: PersistedTx): Promise<void> {
  const db = await openIdb();
  if (!db) return;
  try {
    const t = db.transaction(STORE_QUEUE, "readwrite");
    await idbReq(t.objectStore(STORE_QUEUE).put(tx));
  } catch {
    /* persistance best-effort */
  }
}

async function idbDeleteTx(fileId: string): Promise<void> {
  const db = await openIdb();
  if (!db) return;
  try {
    const t = db.transaction([STORE_QUEUE, STORE_HANDLES], "readwrite");
    t.objectStore(STORE_QUEUE).delete(fileId);
    t.objectStore(STORE_HANDLES).delete(fileId);
  } catch {
    /* noop */
  }
}

async function idbAllTx(): Promise<PersistedTx[]> {
  const db = await openIdb();
  if (!db) return [];
  try {
    const t = db.transaction(STORE_QUEUE, "readonly");
    return (await idbReq(t.objectStore(STORE_QUEUE).getAll())) as PersistedTx[];
  } catch {
    return [];
  }
}

// FileSystemHandle est structured-cloneable → stockable tel quel en IndexedDB.
async function idbPutHandle(fileId: string, handle: FileSystemHandle): Promise<void> {
  const db = await openIdb();
  if (!db) return;
  try {
    const t = db.transaction(STORE_HANDLES, "readwrite");
    await idbReq(t.objectStore(STORE_HANDLES).put({ fileId, handle }));
  } catch {
    /* noop */
  }
}

async function idbGetHandle(fileId: string): Promise<FileSystemHandle | null> {
  const db = await openIdb();
  if (!db) return null;
  try {
    const t = db.transaction(STORE_HANDLES, "readonly");
    const row = (await idbReq(t.objectStore(STORE_HANDLES).get(fileId))) as
      | { fileId: string; handle: FileSystemHandle }
      | undefined;
    return row?.handle ?? null;
  } catch {
    return null;
  }
}

// ════════════════════════════════════════════════════════════════════════════
// Types navigation + transferts
// ════════════════════════════════════════════════════════════════════════════
interface FsEntry {
  name: string;
  type: "file" | "directory";
  size: number | null;
  modified: string | null;
  reparsePoint?: boolean;
  // LOCAL : handle direct ; HOST : chemin absolu.
  handle?: FileSystemHandle;
  path?: string;
}

type SortKey = "name" | "size" | "type" | "modified";

interface FileListResult {
  path: string;
  entries: Array<{ name: string; type: "file" | "directory"; size: number; modified: string; reparsePoint?: boolean }>;
}
interface DriveInfo {
  name: string;
  label: string;
  type: string;
  format: string;
  totalBytes: number;
  freeBytes: number;
  ready: boolean;
}
interface FileDrivesResult {
  drives: DriveInfo[];
}
interface FileSearchResult {
  root: string;
  pattern: string;
  count: number;
  truncated: boolean;
  matches: Array<{ name: string; path: string; type: "file" | "directory"; size: number; modified: string }>;
}
// Recherche via index Windows (Search.CollatorDSO sur SystemIndex). Voir spec V3.
interface FileSearchIndexResult {
  engine: "windows_index";
  indexed: boolean;
  available: boolean;
  count: number;
  truncated: boolean;
  matches: Array<{ name: string; path: string; type: "file" | "directory"; size: number; modified: string }>;
  warning?: string;
}
// Mode de recherche host : "name" = noms (index→fallback enumeration), "content" = plein texte (index seul).
type HostSearchMode = "name" | "content";
// Moteur effectivement utilisé pour le dernier résultat affiché.
type HostSearchEngine = "index" | "walk" | null;

type TxStatus = "queued" | "hashing" | "active" | "paused" | "done" | "error" | "cancelled";

interface Upload {
  fileId: string;
  file: File | null; // null si restauré sans handle réacquis
  name: string;
  size: number;
  mime: string;
  sha256: string | null;
  sent: number;
  acked: number;
  status: TxStatus;
  error?: string;
  integrityOk?: boolean;
  rate: number;
  startedAt: number;
  paused: boolean;
  cancelled: boolean;
  hostPath?: string; // dossier host de destination
  needsHandle?: boolean; // restauré mais le File n'est plus accessible
}

type RxStatus = "queued" | "active" | "done" | "error" | "cancelled" | "paused";

interface Download {
  fileId: string;
  name: string;
  size: number;
  mime: string;
  expectedSha: string;
  received: number;
  status: RxStatus;
  error?: string;
  integrityOk?: boolean;
  rate: number;
  startedAt: number;
  hostPath?: string; // chemin source host
  localDirHandle?: FileSystemDirectoryHandle; // dossier local cible (FSA)
  writer: FileSystemWritableFileStream | null;
  chunks: Uint8Array[] | null;
  objectUrl: string | null;
  hashBuf: Uint8Array[] | null;
  lastAckAt: number;
  ackedSinceLast: number;
}

// Détecte File System Access API (lecture/écriture par handle).
function hasFsAccess(): boolean {
  return (
    typeof (window as unknown as { showDirectoryPicker?: unknown }).showDirectoryPicker === "function" &&
    typeof (window as unknown as { showSaveFilePicker?: unknown }).showSaveFilePicker === "function"
  );
}

// Wrappers typés FSA (les libs DOM n'exposent pas toujours ces signatures).
type DirHandle = FileSystemDirectoryHandle & {
  entries(): AsyncIterableIterator<[string, FileSystemHandle]>;
  queryPermission?(o: { mode: "read" | "readwrite" }): Promise<PermissionState>;
  requestPermission?(o: { mode: "read" | "readwrite" }): Promise<PermissionState>;
};
type FileHandleEx = FileSystemFileHandle & {
  queryPermission?(o: { mode: "read" | "readwrite" }): Promise<PermissionState>;
  requestPermission?(o: { mode: "read" | "readwrite" }): Promise<PermissionState>;
};

async function ensurePermission(handle: FileSystemHandle, mode: "read" | "readwrite"): Promise<boolean> {
  const h = handle as unknown as {
    queryPermission?(o: { mode: string }): Promise<PermissionState>;
    requestPermission?(o: { mode: string }): Promise<PermissionState>;
  };
  try {
    if (h.queryPermission) {
      const q = await h.queryPermission({ mode });
      if (q === "granted") return true;
    }
    if (h.requestPermission) {
      const r = await h.requestPermission({ mode });
      return r === "granted";
    }
  } catch {
    /* noop */
  }
  return false;
}

// ════════════════════════════════════════════════════════════════════════════
// Composant
// ════════════════════════════════════════════════════════════════════════════
const FilesPanel: Component = () => {
  const ws = useWs();
  const tools = useToolClient();

  // ── État transferts (signaux réactifs synchronisés depuis les Maps) ─────────
  const [uploads, setUploads] = createSignal<Upload[]>([]);
  const [downloads, setDownloads] = createSignal<Download[]>([]);
  const downloadState = new Map<string, Download>();
  const uploadState = new Map<string, Upload>();
  const syncUploads = () => setUploads([...uploadState.values()]);
  const syncDownloads = () => setDownloads([...downloadState.values()]);

  // ── État pane HOST (droite) ─────────────────────────────────────────────────
  const [hostPath, setHostPath] = createSignal<string | null>(null); // null = racine (drives)
  const [hostEntries, setHostEntries] = createSignal<FsEntry[]>([]);
  const [hostLoading, setHostLoading] = createSignal(false);
  const [hostError, setHostError] = createSignal<string | null>(null);
  const [hostSearch, setHostSearch] = createSignal("");
  const [hostSearching, setHostSearching] = createSignal(false);
  const [hostSearchMode, setHostSearchMode] = createSignal<HostSearchMode>("name");
  const [hostSearchEngine, setHostSearchEngine] = createSignal<HostSearchEngine>(null);
  const [hostSearchWarning, setHostSearchWarning] = createSignal<string | null>(null);
  const [hostSort, setHostSort] = createSignal<{ key: SortKey; dir: 1 | -1 }>({ key: "name", dir: 1 });
  const [hostSelected, setHostSelected] = createSignal<Set<string>>(new Set());
  const [hostDragOver, setHostDragOver] = createSignal(false);
  const hostCache = new Map<string, FsEntry[]>(); // path → entries
  const [hostHistory, setHostHistory] = createSignal<{ stack: (string | null)[]; idx: number }>({
    stack: [null],
    idx: 0,
  });

  // ── État pane LOCAL (gauche) ────────────────────────────────────────────────
  const [localRoot, setLocalRoot] = createSignal<DirHandle | null>(null);
  const [localStack, setLocalStack] = createSignal<DirHandle[]>([]); // chemin de dirs depuis root
  const [localEntries, setLocalEntries] = createSignal<FsEntry[]>([]);
  const [localLoading, setLocalLoading] = createSignal(false);
  const [localError, setLocalError] = createSignal<string | null>(null);
  const [localSearch, setLocalSearch] = createSignal("");
  const [localSort, setLocalSort] = createSignal<{ key: SortKey; dir: 1 | -1 }>({ key: "name", dir: 1 });
  const [localSelected, setLocalSelected] = createSignal<Set<string>>(new Set());
  const [localDragOver, setLocalDragOver] = createSignal(false);
  const fsaAvailable = hasFsAccess();
  let localFileInput: HTMLInputElement | undefined;

  // Policy fileTransferAllowed (gate optionnel).
  const transferAllowed = createMemo(() => {
    const p = ws.state.hostPolicy;
    if (!p || typeof p.fileTransferAllowed === "undefined") return true;
    return p.fileTransferAllowed !== false;
  });

  // ── Tri générique ───────────────────────────────────────────────────────────
  function sortEntries(entries: FsEntry[], s: { key: SortKey; dir: 1 | -1 }): FsEntry[] {
    const out = [...entries];
    out.sort((a, b) => {
      // dossiers d'abord, toujours.
      if (a.type !== b.type) return a.type === "directory" ? -1 : 1;
      let cmp = 0;
      switch (s.key) {
        case "size":
          cmp = (a.size ?? 0) - (b.size ?? 0);
          break;
        case "type":
          cmp = extOf(a.name).localeCompare(extOf(b.name));
          break;
        case "modified":
          cmp = (a.modified ?? "").localeCompare(b.modified ?? "");
          break;
        default:
          cmp = a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" });
      }
      return cmp * s.dir;
    });
    return out;
  }
  function toggleSort(setter: typeof setHostSort, cur: () => { key: SortKey; dir: 1 | -1 }, key: SortKey): void {
    const c = cur();
    setter(c.key === key ? { key, dir: (c.dir === 1 ? -1 : 1) as 1 | -1 } : { key, dir: 1 });
  }

  // ════════════════════════════════════════════════════════════════════════
  // Pane HOST — navigation via tools file_drives / file_list / file_search
  // ════════════════════════════════════════════════════════════════════════
  async function loadHost(path: string | null, useCache = true): Promise<void> {
    setHostError(null);
    setHostSearching(false);
    setHostSearch("");
    setHostSearchEngine(null);
    setHostSearchWarning(null);
    setHostSelected(new Set<string>());
    if (path !== null && useCache && hostCache.has(path)) {
      setHostEntries(hostCache.get(path)!);
      return;
    }
    setHostLoading(true);
    try {
      if (path === null) {
        const r = await tools.call<FileDrivesResult>("file_drives", {});
        const entries: FsEntry[] = (r.drives || []).map((d) => ({
          name: d.name,
          type: "directory",
          size: d.ready ? d.totalBytes : null,
          modified: null,
          path: d.name, // ex. "C:\\"
        }));
        setHostEntries(entries);
      } else {
        const r = await tools.call<FileListResult>("file_list", { path });
        const entries: FsEntry[] = (r.entries || []).map((e) => ({
          name: e.name,
          type: e.type,
          size: e.type === "file" ? e.size : null,
          modified: e.modified,
          reparsePoint: e.reparsePoint,
          path: joinHost(path, e.name),
        }));
        hostCache.set(path, entries);
        setHostEntries(entries);
      }
    } catch (e) {
      setHostError(humanizeError(e));
      setHostEntries([]);
    } finally {
      setHostLoading(false);
    }
  }

  function joinHost(dir: string, name: string): string {
    if (!dir) return name;
    return dir.endsWith("\\") || dir.endsWith("/") ? dir + name : `${dir}\\${name}`;
  }
  function hostParent(path: string): string | null {
    // "C:\\a\\b" → "C:\\a" ; "C:\\a" → "C:\\" ; "C:\\" → null (racine drives)
    const trimmed = path.replace(/[\\/]+$/, "");
    const idx = Math.max(trimmed.lastIndexOf("\\"), trimmed.lastIndexOf("/"));
    if (idx < 0) return null;
    if (idx <= 2 && /^[A-Za-z]:$/.test(trimmed.slice(0, 2))) return `${trimmed.slice(0, 2)}\\`;
    return trimmed.slice(0, idx) || null;
  }
  function humanizeError(e: unknown): string {
    const m = e instanceof Error ? e.message : String(e);
    if (/denied|access|refus/i.test(m)) return "Accès refusé à ce dossier.";
    if (/timeout/i.test(m)) return "Délai dépassé (host injoignable ?).";
    return m;
  }

  function navHost(path: string | null, pushHistory = true): void {
    setHostPath(path);
    if (pushHistory) {
      const h = hostHistory();
      const stack = h.stack.slice(0, h.idx + 1);
      stack.push(path);
      setHostHistory({ stack, idx: stack.length - 1 });
    }
    void loadHost(path);
  }
  function hostBack(): void {
    const h = hostHistory();
    if (h.idx <= 0) return;
    const idx = h.idx - 1;
    setHostHistory({ ...h, idx });
    setHostPath(h.stack[idx]);
    void loadHost(h.stack[idx]);
  }
  function hostForward(): void {
    const h = hostHistory();
    if (h.idx >= h.stack.length - 1) return;
    const idx = h.idx + 1;
    setHostHistory({ ...h, idx });
    setHostPath(h.stack[idx]);
    void loadHost(h.stack[idx]);
  }
  function hostUp(): void {
    const p = hostPath();
    if (p === null) return;
    navHost(hostParent(p));
  }
  function openHostEntry(en: FsEntry): void {
    if (en.type === "directory" && en.path) navHost(en.path);
  }
  // Transforme la saisie en glob pour l'enumeration file_search ; brut pour l'index.
  function toGlob(q: string): string {
    return /[*?]/.test(q) ? q : `*${q}*`;
  }
  const mapMatches = (
    matches: Array<{ name: string; path: string; type: "file" | "directory"; size: number; modified: string }>,
  ): FsEntry[] =>
    (matches || []).map((m) => ({
      name: m.name,
      type: m.type,
      size: m.type === "file" ? m.size : null,
      modified: m.modified,
      path: m.path,
    }));

  // Mode "name" via enumeration file_search (fallback ou index indispo).
  async function runHostSearchEnumeration(dir: string, query: string): Promise<void> {
    const r = await tools.call<FileSearchResult>("file_search", {
      path: dir,
      pattern: toGlob(query),
      maxResults: SEARCH_MAX_RESULTS,
      includeHidden: false,
      filesOnly: false,
    });
    setHostEntries(mapMatches(r.matches));
    setHostSearchEngine("walk");
    if (r.truncated) setHostSearchWarning(`Résultats tronqués à ${SEARCH_MAX_RESULTS}.`);
  }

  async function runHostSearch(): Promise<void> {
    const query = hostSearch().trim();
    const dir = hostPath();
    if (!query || dir === null) return;
    const mode = hostSearchMode();
    setHostLoading(true);
    setHostSearching(true);
    setHostError(null);
    setHostSearchEngine(null);
    setHostSearchWarning(null);
    try {
      // Tente d'abord l'index Windows (noms ET contenu).
      const idx = await tools.call<FileSearchIndexResult>("file_search_index", {
        scope: dir,
        query,
        mode,
        maxResults: SEARCH_MAX_RESULTS,
      });
      if (idx.available) {
        setHostEntries(mapMatches(idx.matches));
        setHostSearchEngine("index");
        if (idx.warning) setHostSearchWarning(idx.warning);
        else if (idx.truncated) setHostSearchWarning(`Résultats tronqués à ${SEARCH_MAX_RESULTS}.`);
      } else if (mode === "name") {
        // Index indispo → fallback enumeration pour les noms.
        if (idx.warning) setHostSearchWarning(idx.warning);
        await runHostSearchEnumeration(dir, query);
      } else {
        // Contenu sans index → pas d'enumeration possible.
        setHostEntries([]);
        setHostSearchEngine(null);
        setHostError(
          idx.warning ??
            "Contenu non indexé pour ce chemin (active l'indexation Windows ou utilise la recherche par noms).",
        );
      }
    } catch (e) {
      // file_search_index absent/erreur : pour les noms on retombe sur l'enumeration.
      if (mode === "name") {
        try {
          await runHostSearchEnumeration(dir, query);
        } catch (e2) {
          setHostError(humanizeError(e2));
        }
      } else {
        setHostError(humanizeError(e));
      }
    } finally {
      setHostLoading(false);
    }
  }

  const hostBreadcrumb = createMemo<{ label: string; path: string | null }[]>(() => {
    const p = hostPath();
    const crumbs: { label: string; path: string | null }[] = [{ label: "Lecteurs", path: null }];
    if (p === null) return crumbs;
    // ex. "C:\\a\\b" → ["C:", "a", "b"]
    const drive = /^([A-Za-z]:)/.exec(p)?.[1];
    let acc = drive ? `${drive}\\` : "";
    if (drive) crumbs.push({ label: drive, path: acc });
    const rest = p
      .slice(acc.length)
      .replace(/[\\/]+$/, "")
      .split(/[\\/]+/)
      .filter(Boolean);
    for (const seg of rest) {
      acc = joinHost(acc, seg);
      crumbs.push({ label: seg, path: acc });
    }
    return crumbs;
  });

  const hostDisplay = createMemo(() => sortEntries(hostEntries(), hostSort()));

  // ════════════════════════════════════════════════════════════════════════
  // Pane LOCAL — navigation via File System Access API
  // ════════════════════════════════════════════════════════════════════════
  async function pickLocalRoot(): Promise<void> {
    if (!fsaAvailable) return;
    try {
      const handle = (await (
        window as unknown as { showDirectoryPicker: (o?: unknown) => Promise<DirHandle> }
      ).showDirectoryPicker({ mode: "readwrite" })) as DirHandle;
      setLocalRoot(handle);
      setLocalStack([]);
      await listLocal(handle);
    } catch {
      /* annulé par l'utilisateur */
    }
  }

  function currentLocalDir(): DirHandle | null {
    const stack = localStack();
    return stack.length ? stack[stack.length - 1] : localRoot();
  }

  async function listLocal(dir: DirHandle): Promise<void> {
    setLocalError(null);
    setLocalLoading(true);
    setLocalSelected(new Set<string>());
    setLocalSearch("");
    try {
      const out: FsEntry[] = [];
      for await (const [name, handle] of dir.entries()) {
        if (handle.kind === "directory") {
          out.push({ name, type: "directory", size: null, modified: null, handle });
        } else {
          let size: number | null = null;
          let modified: string | null = null;
          try {
            const f = await (handle as FileSystemFileHandle).getFile();
            size = f.size;
            modified = new Date(f.lastModified).toISOString();
          } catch {
            /* méta indispo */
          }
          out.push({ name, type: "file", size, modified, handle });
        }
      }
      setLocalEntries(out);
    } catch (e) {
      setLocalError(humanizeError(e));
      setLocalEntries([]);
    } finally {
      setLocalLoading(false);
    }
  }

  async function openLocalEntry(en: FsEntry): Promise<void> {
    if (en.type !== "directory" || !en.handle) return;
    const dir = en.handle as DirHandle;
    setLocalStack([...localStack(), dir]);
    await listLocal(dir);
  }
  async function localNavToDepth(depth: number): Promise<void> {
    // depth -1 = root ; depth i = localStack[i]
    if (depth < 0) {
      setLocalStack([]);
      const r = localRoot();
      if (r) await listLocal(r);
      return;
    }
    const stack = localStack().slice(0, depth + 1);
    setLocalStack(stack);
    await listLocal(stack[stack.length - 1]);
  }
  async function localUp(): Promise<void> {
    const stack = localStack();
    if (!stack.length) return;
    await localNavToDepth(stack.length - 2);
  }
  async function refreshLocal(): Promise<void> {
    const d = currentLocalDir();
    if (d) await listLocal(d);
  }

  // Recherche locale = filtre récursif borné (max 800 entrées scannées).
  async function runLocalSearch(): Promise<void> {
    const pat = localSearch().trim().toLowerCase();
    const dir = currentLocalDir();
    if (!dir) return;
    if (!pat) {
      await listLocal(dir);
      return;
    }
    setLocalLoading(true);
    setLocalError(null);
    const out: FsEntry[] = [];
    let scanned = 0;
    const MAX_SCAN = 800;
    async function walk(d: DirHandle, depth: number): Promise<void> {
      if (scanned >= MAX_SCAN || depth > 4) return;
      for await (const [name, handle] of d.entries()) {
        if (scanned >= MAX_SCAN) return;
        scanned++;
        if (name.toLowerCase().includes(pat)) {
          if (handle.kind === "file") {
            let size: number | null = null;
            let modified: string | null = null;
            try {
              const f = await (handle as FileSystemFileHandle).getFile();
              size = f.size;
              modified = new Date(f.lastModified).toISOString();
            } catch {
              /* noop */
            }
            out.push({ name, type: "file", size, modified, handle });
          } else {
            out.push({ name, type: "directory", size: null, modified: null, handle });
          }
        }
        if (handle.kind === "directory") await walk(handle as DirHandle, depth + 1);
      }
    }
    try {
      await walk(dir, 0);
      setLocalEntries(out);
      if (scanned >= MAX_SCAN) setLocalError(`Recherche limitée à ${MAX_SCAN} entrées.`);
    } catch (e) {
      setLocalError(humanizeError(e));
    } finally {
      setLocalLoading(false);
    }
  }

  const localBreadcrumb = createMemo<{ label: string; depth: number }[]>(() => {
    const r = localRoot();
    if (!r) return [];
    const crumbs = [{ label: r.name || "Racine", depth: -1 }];
    localStack().forEach((d, i) => crumbs.push({ label: d.name, depth: i }));
    return crumbs;
  });
  const localDisplay = createMemo(() => sortEntries(localEntries(), localSort()));

  // ════════════════════════════════════════════════════════════════════════
  // Capability + handlers WS (réception FB + contrôle file:*)
  // ════════════════════════════════════════════════════════════════════════
  function announceFileCapabilities(): void {
    if (ws.isOpen()) ws.send({ type: "operator:capabilities", capabilities: { fileBinary: true } });
  }

  onMount(() => {
    announceFileCapabilities();
    void loadHost(null);
    void restoreQueue();
  });

  createEffect(() => {
    if (ws.state.wsStatus !== "open") return;
    announceFileCapabilities();
    void pumpQueue();
  });

  const offBinary = ws.onBinary((data) => {
    const fb = parseFbFrame(data);
    if (!fb) return; // pas FB → vidéo/RDP/MB : ignorer
    const dl = downloadState.get(fb.fileId);
    if (!dl || dl.status !== "active") return;

    if (fb.offset !== dl.received) {
      if (fb.offset < dl.received) return; // doublon déjà écrit
      dl.status = "error";
      ws.send({ type: "file:error", fileId: fb.fileId, code: "seq", message: "offset hors séquence" });
      syncDownloads();
      return;
    }

    if (dl.writer) {
      const out = new ArrayBuffer(fb.payload.length);
      new Uint8Array(out).set(fb.payload);
      void dl.writer.write(out).catch((e) => {
        dl.status = "error";
        dl.error = e instanceof Error ? e.message : String(e);
        syncDownloads();
      });
    } else if (dl.chunks) {
      dl.chunks.push(fb.payload.slice());
    }
    if (dl.hashBuf) dl.hashBuf.push(fb.payload.slice());

    dl.received += fb.payload.length;
    dl.ackedSinceLast += fb.payload.length;
    const now = performance.now();
    const elapsed = (now - dl.startedAt) / 1000;
    dl.rate = elapsed > 0 ? dl.received / elapsed : 0;

    if (dl.ackedSinceLast >= ACK_INTERVAL_BYTES || now - dl.lastAckAt >= 500 || fb.last) {
      ws.send({ type: "file:ack", fileId: fb.fileId, ackedOffset: dl.received });
      dl.lastAckAt = now;
      dl.ackedSinceLast = 0;
      void persistDownload(dl);
    }
    syncDownloads();
    if (fb.last) void finalizeDownload(dl);
  });
  onCleanup(offBinary);

  async function finalizeDownload(dl: Download): Promise<void> {
    try {
      if (dl.writer) await dl.writer.close();
      let ok = true;
      if (dl.hashBuf && dl.hashBuf.length && dl.expectedSha) {
        const total = dl.hashBuf.reduce((s, c) => s + c.length, 0);
        const merged = new Uint8Array(total);
        let off = 0;
        for (const c of dl.hashBuf) {
          merged.set(c, off);
          off += c.length;
        }
        const digest = await crypto.subtle.digest("SHA-256", merged.buffer);
        ok = toHex(digest).toLowerCase() === dl.expectedSha.toLowerCase();
        dl.hashBuf = null;
      }
      if (!dl.writer && dl.chunks) {
        const parts = dl.chunks.map((c) => {
          const ab = new ArrayBuffer(c.length);
          new Uint8Array(ab).set(c);
          return ab;
        });
        const blob = new Blob(parts, { type: dl.mime || "application/octet-stream" });
        dl.objectUrl = URL.createObjectURL(blob);
        dl.chunks = null;
      }
      dl.integrityOk = ok;
      dl.status = ok ? "done" : "error";
      if (!ok) dl.error = "Intégrité sha256 invalide";
      ws.send({ type: "file:complete", fileId: dl.fileId, ok, sha256: dl.expectedSha });
      if (ok) void idbDeleteTx(dl.fileId);
      else void persistDownload(dl);
      // Rafraîchir le dossier local si on a écrit dedans.
      if (dl.localDirHandle && currentLocalDir() === dl.localDirHandle) void refreshLocal();
    } catch (e) {
      dl.status = "error";
      dl.error = e instanceof Error ? e.message : String(e);
      ws.send({ type: "file:complete", fileId: dl.fileId, ok: false, sha256: dl.expectedSha });
    }
    syncDownloads();
  }

  const offCtrl = ws.on(
    (m) => typeof m.type === "string" && m.type.startsWith("file:"),
    (m) => {
      const msg = m as Record<string, unknown> & { type: string };
      if (msg.type === "file:pending") {
        handlePending(msg);
        return;
      }
      const fileId = String(msg.fileId ?? "");
      if (!fileId) return;

      if (msg.type === "file:offer") {
        void handleIncomingOffer(msg);
        return;
      }
      if (msg.type === "file:resume") {
        const up = uploadState.get(fileId);
        if (up) {
          up.acked = Number(msg.ackedOffset ?? 0);
          up.sent = up.acked;
          syncUploads();
        }
        return;
      }
      if (msg.type === "file:ack") {
        const up = uploadState.get(fileId);
        if (up) {
          up.acked = Number(msg.ackedOffset ?? up.acked);
          void persistUpload(up);
          syncUploads();
        }
        return;
      }
      if (msg.type === "file:complete") {
        const up = uploadState.get(fileId);
        if (up) {
          up.integrityOk = msg.ok === true;
          up.status = msg.ok === true ? "done" : "error";
          if (msg.ok !== true) up.error = "Récepteur : intégrité invalide";
          if (msg.ok === true) void idbDeleteTx(fileId);
          syncUploads();
        }
        return;
      }
      if (msg.type === "file:cancel" || msg.type === "file:error") {
        const up = uploadState.get(fileId);
        if (up && up.status !== "done") {
          up.cancelled = true;
          up.status = "cancelled";
          up.error = String(msg.message ?? msg.reason ?? "annulé par le pair");
          syncUploads();
        }
        const dl = downloadState.get(fileId);
        if (dl && dl.status === "active") {
          dl.status = "cancelled";
          if (dl.writer) void dl.writer.abort?.().catch(() => undefined);
          syncDownloads();
        }
        return;
      }
    },
  );
  onCleanup(offCtrl);

  // file:pending : l'host annonce ses transferts incomplets après reboot/reconnect.
  function handlePending(msg: Record<string, unknown>): void {
    const transfers = (msg.transfers as Array<Record<string, unknown>>) || [];
    for (const t of transfers) {
      const fileId = String(t.fileId ?? "");
      if (!fileId) continue;
      const direction = String(t.direction ?? ""); // recv (host reçoit) | send (host émet)
      const acked = Number(t.ackedOffset ?? 0);
      if (direction === "recv") {
        // host attend la SUITE d'un upload : on relance l'offer si on le connaît.
        const up = uploadState.get(fileId);
        if (up && up.status !== "done") {
          up.acked = acked;
          up.sent = acked;
          if (up.file && (up.status === "error" || up.status === "cancelled" || up.status === "paused")) {
            up.status = "queued";
            up.cancelled = false;
            up.paused = false;
            void pumpQueue();
          }
          syncUploads();
        }
      } else if (direction === "send") {
        // host peut reprendre l'émission d'un download : on re-demande.
        const dl = downloadState.get(fileId);
        if (dl && dl.status !== "done" && dl.hostPath) {
          ws.send({ type: "file:request", fileId, path: dl.hostPath, chunkSize: CHUNK_SIZE });
        }
      }
    }
  }

  // ── Réception : ouverture du sink à l'offer (peut être déclenché par notre
  //    file:request OU un offer spontané de l'host). ─────────────────────────
  async function handleIncomingOffer(msg: Record<string, unknown>): Promise<void> {
    const fileId = String(msg.fileId);
    const name = sanitizeFilename(String(msg.name ?? "fichier")) ?? "fichier";
    const size = Number(msg.size ?? 0);
    const mime = String(msg.mime ?? "application/octet-stream");
    const expectedSha = String(msg.sha256 ?? "");

    if (size > FILE_MAX_BYTES) {
      ws.send({ type: "file:error", fileId, code: "too_large", message: "Fichier trop volumineux" });
      return;
    }
    if (!isAllowedMime(mime)) {
      ws.send({ type: "file:error", fileId, code: "mime", message: `Type refusé : ${mime}` });
      return;
    }

    // Pré-créé par un file:request explicite (on a déjà le dossier local cible) ?
    const existing = downloadState.get(fileId);
    let writer: FileSystemWritableFileStream | null = null;
    let chunks: Uint8Array[] | null = null;
    const localDirHandle: FileSystemDirectoryHandle | undefined = existing?.localDirHandle;

    if (localDirHandle) {
      try {
        const fh = (await localDirHandle.getFileHandle(name, { create: true })) as FileSystemFileHandle;
        writer = await fh.createWritable();
        void idbPutHandle(fileId, fh);
      } catch {
        chunks = [];
      }
    } else if (hasFsAccess()) {
      try {
        const handle = await (
          window as unknown as {
            showSaveFilePicker: (o: unknown) => Promise<{
              createWritable: () => Promise<FileSystemWritableFileStream>;
            }>;
          }
        ).showSaveFilePicker({ suggestedName: name });
        writer = await handle.createWritable();
      } catch {
        chunks = [];
      }
    } else {
      chunks = [];
    }

    const dl: Download = {
      fileId,
      name,
      size,
      mime,
      expectedSha,
      received: 0,
      status: "active",
      rate: 0,
      startedAt: performance.now(),
      hostPath: existing?.hostPath,
      localDirHandle,
      writer,
      chunks,
      objectUrl: null,
      hashBuf: expectedSha ? [] : null,
      lastAckAt: performance.now(),
      ackedSinceLast: 0,
    };
    downloadState.set(fileId, dl);
    void persistDownload(dl);
    syncDownloads();

    // v1 navigateur ne reprend pas le partiel disque écrit → resume 0.
    ws.send({ type: "file:resume", fileId, ackedOffset: 0 });
  }

  // ════════════════════════════════════════════════════════════════════════
  // Persistance (P3)
  // ════════════════════════════════════════════════════════════════════════
  function persistUpload(up: Upload): Promise<void> {
    return idbPutTx({
      fileId: up.fileId,
      direction: "upload",
      name: up.name,
      size: up.size,
      mime: up.mime,
      sha256: up.sha256,
      acked: up.acked,
      status: up.status,
      hostPath: up.hostPath,
      updatedAt: Date.now(),
    });
  }
  function persistDownload(dl: Download): Promise<void> {
    return idbPutTx({
      fileId: dl.fileId,
      direction: "download",
      name: dl.name,
      size: dl.size,
      mime: dl.mime,
      sha256: dl.expectedSha,
      acked: dl.received,
      status: dl.status,
      hostPath: dl.hostPath,
      updatedAt: Date.now(),
    });
  }

  async function restoreQueue(): Promise<void> {
    const rows = await idbAllTx();
    for (const row of rows) {
      if (row.status === "done") {
        void idbDeleteTx(row.fileId);
        continue;
      }
      if (row.direction === "upload") {
        const handle = (await idbGetHandle(row.fileId)) as FileSystemFileHandle | null;
        let file: File | null = null;
        let needsHandle = true;
        if (handle) {
          const granted = await ensurePermission(handle, "read");
          if (granted) {
            try {
              file = await (handle as FileHandleEx).getFile();
              needsHandle = false;
            } catch {
              /* fichier déplacé/supprimé */
            }
          }
        }
        const up: Upload = {
          fileId: row.fileId,
          file,
          name: row.name,
          size: row.size,
          mime: row.mime,
          sha256: row.sha256,
          sent: row.acked,
          acked: row.acked,
          status: file ? "queued" : "paused",
          needsHandle,
          rate: 0,
          startedAt: 0,
          paused: !file,
          cancelled: false,
          hostPath: row.hostPath,
          error: file ? undefined : "Reprise : re-sélectionner le fichier source.",
        };
        uploadState.set(up.fileId, up);
      } else {
        // download : on attend file:pending de l'host ou une action manuelle.
        const dirHandle = (await idbGetHandle(row.fileId)) as FileSystemDirectoryHandle | null;
        const dl: Download = {
          fileId: row.fileId,
          name: row.name,
          size: row.size,
          mime: row.mime,
          expectedSha: row.sha256 ?? "",
          received: 0, // navigateur ne reprend pas le partiel disque (resume 0)
          status: "paused",
          rate: 0,
          startedAt: 0,
          hostPath: row.hostPath,
          localDirHandle: dirHandle ?? undefined,
          writer: null,
          chunks: null,
          objectUrl: null,
          hashBuf: null,
          lastAckAt: 0,
          ackedSinceLast: 0,
          error: "Reprise en attente (cliquer Reprendre).",
        };
        downloadState.set(dl.fileId, dl);
      }
    }
    syncUploads();
    syncDownloads();
    void pumpQueue();
  }

  // ════════════════════════════════════════════════════════════════════════
  // UPLOAD (LOCAL → HOST)
  // ════════════════════════════════════════════════════════════════════════
  function enqueueUploadFile(file: File, hostDir: string | null, handle?: FileSystemFileHandle): void {
    if (file.size > FILE_MAX_BYTES) {
      addRejected(file.name, `Trop volumineux (max ${FILE_MAX_BYTES / 1024 / 1024} Mo)`);
      return;
    }
    const mime = file.type || "application/octet-stream";
    if (!isAllowedMime(mime)) {
      addRejected(file.name, `Type refusé : ${mime}`);
      return;
    }
    const safeName = sanitizeFilename(file.name);
    if (!safeName) {
      addRejected(file.name, "Nom invalide");
      return;
    }
    const up: Upload = {
      fileId: safeUuid(),
      file,
      name: safeName,
      size: file.size,
      mime,
      sha256: null,
      sent: 0,
      acked: 0,
      status: "queued",
      rate: 0,
      startedAt: 0,
      paused: false,
      cancelled: false,
      hostPath: hostDir ?? undefined,
    };
    uploadState.set(up.fileId, up);
    void persistUpload(up);
    if (handle) void idbPutHandle(up.fileId, handle);
    syncUploads();
    void pumpQueue();
  }

  function addRejected(name: string, reason: string): void {
    const id = safeUuid();
    uploadState.set(id, {
      fileId: id,
      file: null,
      name,
      size: 0,
      mime: "",
      sha256: null,
      sent: 0,
      acked: 0,
      status: "error",
      error: reason,
      rate: 0,
      startedAt: 0,
      paused: false,
      cancelled: false,
    });
    syncUploads();
  }

  let pumping = false;
  async function pumpQueue(): Promise<void> {
    if (pumping) return;
    if (!ws.isOpen()) return;
    pumping = true;
    try {
      for (;;) {
        if (!ws.isOpen()) break;
        const next = [...uploadState.values()].find((u) => u.status === "queued" && u.file);
        if (!next) break;
        await sendUpload(next);
      }
    } finally {
      pumping = false;
    }
  }

  async function waitForBuffer(): Promise<void> {
    let wait = 25;
    while (ws.isOpen() && ws.bufferedAmount() > WS_BUFFER_HIGH_WATER) {
      await new Promise((r) => setTimeout(r, wait));
      wait = Math.min(wait * 2, 500);
    }
  }

  async function sendUpload(up: Upload): Promise<void> {
    if (!up.file) {
      up.status = "paused";
      up.needsHandle = true;
      syncUploads();
      return;
    }
    if (!ws.isOpen()) {
      up.status = "queued";
      syncUploads();
      return;
    }
    try {
      if (!up.sha256) {
        up.status = "hashing";
        syncUploads();
        up.sha256 = await computeSha256Streaming(up.file);
        void persistUpload(up);
      }
      if (!ws.isOpen()) {
        up.status = "queued";
        syncUploads();
        return;
      }
      up.status = "active";
      up.startedAt = performance.now();
      syncUploads();
      // offer — inclut destDir si l'host le supporte (sinon il dépose dans le
      // dossier downloads par défaut côté host).
      ws.send({
        type: "file:offer",
        fileId: up.fileId,
        name: up.name,
        size: up.size,
        mime: up.mime,
        sha256: up.sha256,
        chunkSize: CHUNK_SIZE,
        ...(up.hostPath ? { destDir: up.hostPath } : {}),
      });

      await new Promise((r) => setTimeout(r, 150)); // attend file:resume best-effort
      let offset = up.acked || 0;
      up.sent = offset;

      while (offset < up.size) {
        if (up.cancelled) {
          up.status = "cancelled";
          ws.send({ type: "file:cancel", fileId: up.fileId, reason: "annulé par l'opérateur" });
          void persistUpload(up);
          syncUploads();
          return;
        }
        if (up.paused) {
          up.status = "paused";
          syncUploads();
          await new Promise((r) => setTimeout(r, 200));
          continue;
        }
        if (up.status !== "active") {
          up.status = "active";
          syncUploads();
        }
        if (offset - up.acked >= WINDOW_BYTES) {
          await new Promise((r) => setTimeout(r, 20));
          continue;
        }
        await waitForBuffer();
        if (!ws.isOpen()) {
          up.status = "queued";
          syncUploads();
          return;
        }
        const end = Math.min(offset + CHUNK_SIZE, up.size);
        const slice = up.file.slice(offset, end);
        const payload = new Uint8Array(await slice.arrayBuffer());
        const last = end >= up.size;
        ws.sendBinary(buildFbFrame(up.fileId, offset, payload, last));
        offset = end;
        up.sent = offset;
        const elapsed = (performance.now() - up.startedAt) / 1000;
        up.rate = elapsed > 0 ? up.sent / elapsed : 0;
        syncUploads();
      }

      ws.send({ type: "file:done", fileId: up.fileId, sha256: up.sha256 });
      void persistUpload(up);
    } catch (e) {
      up.status = "error";
      up.error = e instanceof Error ? e.message : String(e);
      void persistUpload(up);
      syncUploads();
    }
  }

  // ════════════════════════════════════════════════════════════════════════
  // DOWNLOAD (HOST → LOCAL)
  // ════════════════════════════════════════════════════════════════════════
  async function startDownload(en: FsEntry, localDir: FileSystemDirectoryHandle | null): Promise<void> {
    if (en.type !== "file" || !en.path) return;
    if ((en.size ?? 0) > FILE_MAX_BYTES) {
      addRejected(en.name, `Trop volumineux (max ${FILE_MAX_BYTES / 1024 / 1024} Mo)`);
      return;
    }
    const fileId = safeUuid();
    // On pré-crée l'entrée download avec le dossier cible : handleIncomingOffer
    // l'utilisera pour ouvrir un WritableStream FSA (sinon showSaveFilePicker).
    const dl: Download = {
      fileId,
      name: sanitizeFilename(en.name) ?? en.name,
      size: en.size ?? 0,
      mime: "application/octet-stream",
      expectedSha: "",
      received: 0,
      status: "queued",
      rate: 0,
      startedAt: 0,
      hostPath: en.path,
      localDirHandle: localDir ?? undefined,
      writer: null,
      chunks: null,
      objectUrl: null,
      hashBuf: null,
      lastAckAt: 0,
      ackedSinceLast: 0,
    };
    downloadState.set(fileId, dl);
    void persistDownload(dl);
    if (localDir) void idbPutHandle(fileId, localDir);
    syncDownloads();
    // Demande à l'host d'émettre → il répond file:offer (même fileId) + FB.
    ws.send({ type: "file:request", fileId, path: en.path, chunkSize: CHUNK_SIZE });
  }

  // ════════════════════════════════════════════════════════════════════════
  // Actions UI transferts
  // ════════════════════════════════════════════════════════════════════════
  function togglePause(up: Upload): void {
    const u = uploadState.get(up.fileId);
    if (!u || u.status === "done" || u.status === "error" || u.status === "cancelled") return;
    if (u.needsHandle && !u.file) return;
    u.paused = !u.paused;
    if (!u.paused && u.status === "paused" && u.file) {
      u.status = "queued";
      void pumpQueue();
    }
    syncUploads();
  }
  function cancelUpload(up: Upload): void {
    const u = uploadState.get(up.fileId);
    if (!u) return;
    u.cancelled = true;
    if (u.status === "queued" || u.status === "hashing" || u.status === "paused") {
      u.status = "cancelled";
      ws.send({ type: "file:cancel", fileId: u.fileId, reason: "annulé" });
    }
    syncUploads();
  }
  function removeUpload(up: Upload): void {
    uploadState.delete(up.fileId);
    void idbDeleteTx(up.fileId);
    syncUploads();
  }
  function removeDownload(dl: Download): void {
    if (dl.objectUrl) URL.revokeObjectURL(dl.objectUrl);
    downloadState.delete(dl.fileId);
    void idbDeleteTx(dl.fileId);
    syncDownloads();
  }
  function resumeDownload(dl: Download): void {
    const d = downloadState.get(dl.fileId);
    if (!d || !d.hostPath) return;
    d.status = "queued";
    d.error = undefined;
    syncDownloads();
    ws.send({ type: "file:request", fileId: d.fileId, path: d.hostPath, chunkSize: CHUNK_SIZE });
  }
  // Re-sélectionner le fichier source pour un upload restauré sans handle.
  async function reselectUpload(up: Upload): Promise<void> {
    const u = uploadState.get(up.fileId);
    if (!u) return;
    try {
      if (fsaAvailable) {
        const [fh] = (await (
          window as unknown as { showOpenFilePicker: (o?: unknown) => Promise<FileSystemFileHandle[]> }
        ).showOpenFilePicker()) as FileSystemFileHandle[];
        u.file = await (fh as FileHandleEx).getFile();
        void idbPutHandle(u.fileId, fh);
      } else {
        return; // pas de FSA → impossible de réacquérir programmatiquement
      }
      u.needsHandle = false;
      u.error = undefined;
      u.paused = false;
      u.status = "queued";
      syncUploads();
      void pumpQueue();
    } catch {
      /* annulé */
    }
  }

  onCleanup(() => {
    for (const dl of downloadState.values()) if (dl.objectUrl) URL.revokeObjectURL(dl.objectUrl);
  });

  // ════════════════════════════════════════════════════════════════════════
  // Drag inter-pane
  // ════════════════════════════════════════════════════════════════════════
  const DRAG_MIME = "application/x-rws-fs";
  type DragPayload = { src: "local" | "host"; names: string[] };

  function startDragFrom(side: "local" | "host", entry: FsEntry, e: DragEvent): void {
    const sel = side === "local" ? localSelected() : hostSelected();
    const names = sel.has(entry.name) ? [...sel] : [entry.name];
    const payload: DragPayload = { src: side, names };
    e.dataTransfer?.setData(DRAG_MIME, JSON.stringify(payload));
    if (e.dataTransfer) e.dataTransfer.effectAllowed = "copy";
  }

  // Drop sur le pane HOST = upload des fichiers locaux déposés.
  async function onDropHost(e: DragEvent): Promise<void> {
    e.preventDefault();
    setHostDragOver(false);
    if (!transferAllowed()) return;
    const hostDir = hostPath();
    if (hostDir === null) {
      setHostError("Choisir un dossier (pas la racine lecteurs) comme destination.");
      return;
    }
    // 1) Drag interne depuis le pane LOCAL.
    const raw = e.dataTransfer?.getData(DRAG_MIME);
    if (raw) {
      try {
        const p = JSON.parse(raw) as DragPayload;
        if (p.src === "local") {
          for (const name of p.names) {
            const en = localEntries().find((x) => x.name === name && x.type === "file");
            if (en?.handle) {
              const f = await (en.handle as FileHandleEx).getFile();
              enqueueUploadFile(f, hostDir, en.handle as FileSystemFileHandle);
            }
          }
          return;
        }
      } catch {
        /* fallthrough */
      }
    }
    // 2) Fichiers OS déposés directement (drag depuis l'explorateur Windows/Finder).
    if (e.dataTransfer?.files?.length) {
      for (const f of Array.from(e.dataTransfer.files)) enqueueUploadFile(f, hostDir);
    }
  }

  // Drop sur le pane LOCAL = download des fichiers host déposés.
  async function onDropLocal(e: DragEvent): Promise<void> {
    e.preventDefault();
    setLocalDragOver(false);
    if (!transferAllowed()) return;
    const raw = e.dataTransfer?.getData(DRAG_MIME);
    if (!raw) return;
    try {
      const p = JSON.parse(raw) as DragPayload;
      if (p.src !== "host") return;
      const dir = currentLocalDir();
      for (const name of p.names) {
        const en = hostEntries().find((x) => x.name === name && x.type === "file");
        if (en) await startDownload(en, dir);
      }
    } catch {
      /* noop */
    }
  }

  // Boutons explicites (accessibilité sans drag).
  async function sendSelectionToHost(): Promise<void> {
    const hostDir = hostPath();
    if (hostDir === null) {
      setHostError("Naviguer dans un dossier host avant l'envoi.");
      return;
    }
    const sel = localSelected();
    for (const en of localEntries()) {
      if (en.type === "file" && sel.has(en.name) && en.handle) {
        const f = await (en.handle as FileHandleEx).getFile();
        enqueueUploadFile(f, hostDir, en.handle as FileSystemFileHandle);
      }
    }
  }
  async function downloadSelectionToLocal(): Promise<void> {
    const dir = currentLocalDir();
    const sel = hostSelected();
    for (const en of hostEntries()) {
      if (en.type === "file" && sel.has(en.name)) await startDownload(en, dir);
    }
  }

  function toggleSelect(side: "local" | "host", name: string, e: MouseEvent): void {
    const cur = side === "local" ? localSelected() : hostSelected();
    const next = new Set<string>(cur);
    if (!(e.ctrlKey || e.metaKey)) {
      // sélection simple : remplace, sauf si on reclique le même
      next.clear();
      next.add(name);
    } else if (next.has(name)) next.delete(name);
    else next.add(name);
    if (side === "local") setLocalSelected(next);
    else setHostSelected(next);
  }

  // ════════════════════════════════════════════════════════════════════════
  // Rendu
  // ════════════════════════════════════════════════════════════════════════
  function statusBadge(s: TxStatus | RxStatus): { cls: string; label: string } {
    switch (s) {
      case "queued":
        return { cls: "files-badge--queued", label: "En file" };
      case "hashing":
        return { cls: "files-badge--queued", label: "Hash…" };
      case "active":
        return { cls: "files-badge--active", label: "En cours" };
      case "paused":
        return { cls: "files-badge--paused", label: "En pause" };
      case "done":
        return { cls: "files-badge--done", label: "Terminé" };
      case "cancelled":
        return { cls: "files-badge--error", label: "Annulé" };
      default:
        return { cls: "files-badge--error", label: "Erreur" };
    }
  }
  function pct(sent: number, size: number): number {
    if (size <= 0) return 100;
    return Math.min(100, Math.round((sent / size) * 100));
  }
  function fillCls(s: TxStatus | RxStatus): string {
    if (s === "done") return "files-progress-fill files-progress-fill--done";
    if (s === "error" || s === "cancelled") return "files-progress-fill files-progress-fill--error";
    if (s === "paused") return "files-progress-fill files-progress-fill--paused";
    return "files-progress-fill";
  }

  // Liste de fichiers (colonnes) générique pour les 2 panes.
  function FileGrid(p: {
    side: "local" | "host";
    entries: () => FsEntry[];
    loading: () => boolean;
    sort: () => { key: SortKey; dir: 1 | -1 };
    onSort: (k: SortKey) => void;
    selected: () => Set<string>;
    onSelect: (name: string, e: MouseEvent) => void;
    onOpen: (en: FsEntry) => void;
  }): JSX.Element {
    const sortArrow = (k: SortKey) => (p.sort().key === k ? (p.sort().dir === 1 ? " ▲" : " ▼") : "");
    return (
      <div class="fb-grid">
        <div class="fb-grid-head">
          <button class="fb-col fb-col--name" onClick={() => p.onSort("name")}>
            Nom{sortArrow("name")}
          </button>
          <button class="fb-col fb-col--size" onClick={() => p.onSort("size")}>
            Taille{sortArrow("size")}
          </button>
          <button class="fb-col fb-col--type" onClick={() => p.onSort("type")}>
            Type{sortArrow("type")}
          </button>
          <button class="fb-col fb-col--date" onClick={() => p.onSort("modified")}>
            Modifié{sortArrow("modified")}
          </button>
        </div>
        <div class="fb-grid-body">
          <Show when={p.loading()}>
            <div class="fb-empty">Chargement…</div>
          </Show>
          <Show when={!p.loading() && p.entries().length === 0}>
            <div class="fb-empty">Dossier vide.</div>
          </Show>
          <For each={p.entries()}>
            {(en) => (
              <div
                class={`fb-row${p.selected().has(en.name) ? " fb-row--sel" : ""}`}
                draggable={en.type === "file"}
                onDragStart={(e) => startDragFrom(p.side, en, e)}
                onClick={(e) => p.onSelect(en.name, e)}
                onDblClick={() => p.onOpen(en)}
                title={en.path || en.name}
              >
                <div class="fb-col fb-col--name">
                  <span class="fb-icon">{en.type === "directory" ? "📁" : fileIcon(en.name)}</span>
                  <span class="fb-name">{en.name}</span>
                </div>
                <div class="fb-col fb-col--size">{en.type === "directory" ? "" : fmtBytes(en.size)}</div>
                <div class="fb-col fb-col--type">
                  {en.type === "directory" ? "Dossier" : extOf(en.name) || "fichier"}
                </div>
                <div class="fb-col fb-col--date">{fmtDate(en.modified)}</div>
              </div>
            )}
          </For>
        </div>
      </div>
    );
  }

  const txCount = createMemo(() => uploads().length + downloads().length);

  return (
    <PanelLayout title="Explorateur de fichiers">
      <div class="fb-explorer">
        <Show when={!transferAllowed()}>
          <div class="files-disabled-note">
            Le transfert de fichiers est désactivé par la politique de l'hôte (fileTransferAllowed = false).
          </div>
        </Show>

        <div class="fb-panes">
          {/* ── PANE LOCAL ─────────────────────────────────────────────── */}
          <div
            class={`fb-pane${localDragOver() ? " fb-pane--dropping" : ""}`}
            onDragOver={(e) => {
              if (e.dataTransfer?.types.includes(DRAG_MIME)) {
                e.preventDefault();
                setLocalDragOver(true);
              }
            }}
            onDragLeave={() => setLocalDragOver(false)}
            onDrop={(e) => void onDropLocal(e)}
          >
            <div class="fb-pane-head">
              <span class="fb-pane-title">💻 Local (opérateur)</span>
              <span class="fb-pane-sub">→ glisser un fichier host ici pour télécharger</span>
            </div>

            <Show
              when={fsaAvailable}
              fallback={
                <div class="fb-fallback">
                  <p>Le navigateur ne supporte pas l'accès au système de fichiers (Safari/Firefox).</p>
                  <p>Vous pouvez tout de même envoyer des fichiers vers l'hôte :</p>
                  <button
                    class="files-btn files-btn--primary"
                    disabled={hostPath() === null}
                    onClick={() => localFileInput?.click()}
                  >
                    Sélectionner des fichiers à envoyer
                  </button>
                  <Show when={hostPath() === null}>
                    <p class="fb-hint">Choisir d'abord un dossier de destination dans le pane host.</p>
                  </Show>
                  <input
                    ref={localFileInput}
                    type="file"
                    multiple
                    style={{ display: "none" }}
                    onChange={(e) => {
                      const hostDir = hostPath();
                      if (e.currentTarget.files && hostDir !== null) {
                        for (const f of Array.from(e.currentTarget.files)) enqueueUploadFile(f, hostDir);
                      }
                      e.currentTarget.value = "";
                    }}
                  />
                </div>
              }
            >
              <Show
                when={localRoot()}
                fallback={
                  <div class="fb-fallback">
                    <button class="files-btn files-btn--primary" onClick={() => void pickLocalRoot()}>
                      📂 Choisir un dossier…
                    </button>
                    <p class="fb-hint">Sélectionnez un dossier local à explorer.</p>
                  </div>
                }
              >
                <div class="fb-toolbar">
                  <button class="fb-tb-btn" title="Dossier parent" onClick={() => void localUp()}>
                    ↑
                  </button>
                  <button class="fb-tb-btn" title="Rafraîchir" onClick={() => void refreshLocal()}>
                    ⟳
                  </button>
                  <button class="fb-tb-btn" title="Changer de dossier racine" onClick={() => void pickLocalRoot()}>
                    📂
                  </button>
                  <div class="fb-search-mode" role="group" aria-label="Mode de recherche local">
                    <button class="fb-search-mode-btn is-active" title="Recherche par noms de fichiers">
                      Noms
                    </button>
                    <button class="fb-search-mode-btn" title="Recherche plein texte indisponible en local" disabled>
                      Contenu
                    </button>
                  </div>
                  <input
                    class="fb-search"
                    placeholder="Rechercher par nom (local)…"
                    value={localSearch()}
                    onInput={(e) => setLocalSearch(e.currentTarget.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") void runLocalSearch();
                    }}
                  />
                  <button class="fb-tb-btn" title="Rechercher" onClick={() => void runLocalSearch()}>
                    🔍
                  </button>
                </div>
                <div class="fb-crumbs">
                  <For each={localBreadcrumb()}>
                    {(c, i) => (
                      <>
                        <Show when={i() > 0}>
                          <span class="fb-crumb-sep">›</span>
                        </Show>
                        <button class="fb-crumb" onClick={() => void localNavToDepth(c.depth)}>
                          {c.label}
                        </button>
                      </>
                    )}
                  </For>
                </div>
                <Show when={localError()}>
                  <div class="fb-error">{localError()}</div>
                </Show>
                <FileGrid
                  side="local"
                  entries={localDisplay}
                  loading={localLoading}
                  sort={localSort}
                  onSort={(k) => toggleSort(setLocalSort, localSort, k)}
                  selected={localSelected}
                  onSelect={(n, e) => toggleSelect("local", n, e)}
                  onOpen={(en) => void openLocalEntry(en)}
                />
                <div class="fb-pane-actions">
                  <button
                    class="files-btn"
                    disabled={localSelected().size === 0}
                    onClick={() => void sendSelectionToHost()}
                  >
                    Envoyer → host
                  </button>
                </div>
              </Show>
            </Show>
          </div>

          {/* ── SPLITTER (visuel ; CSS gère le redimensionnement via resize) ── */}
          <div class="fb-splitter" />

          {/* ── PANE HOST ──────────────────────────────────────────────── */}
          <div
            class={`fb-pane${hostDragOver() ? " fb-pane--dropping" : ""}`}
            onDragOver={(e) => {
              // accepte le drag interne (local) ET les fichiers OS.
              e.preventDefault();
              setHostDragOver(true);
            }}
            onDragLeave={() => setHostDragOver(false)}
            onDrop={(e) => void onDropHost(e)}
          >
            <div class="fb-pane-head">
              <span class="fb-pane-title">🖥 Host (distant)</span>
              <span class="fb-pane-sub">← glisser un fichier local ici pour envoyer</span>
            </div>
            <div class="fb-toolbar">
              <button class="fb-tb-btn" title="Précédent" onClick={hostBack} disabled={hostHistory().idx <= 0}>
                ←
              </button>
              <button
                class="fb-tb-btn"
                title="Suivant"
                onClick={hostForward}
                disabled={hostHistory().idx >= hostHistory().stack.length - 1}
              >
                →
              </button>
              <button class="fb-tb-btn" title="Dossier parent" onClick={hostUp} disabled={hostPath() === null}>
                ↑
              </button>
              <button class="fb-tb-btn" title="Rafraîchir" onClick={() => void loadHost(hostPath(), false)}>
                ⟳
              </button>
              <div class="fb-search-mode" role="group" aria-label="Mode de recherche host">
                <button
                  class={`fb-search-mode-btn${hostSearchMode() === "name" ? " is-active" : ""}`}
                  title="Recherche par noms de fichiers"
                  disabled={hostPath() === null}
                  onClick={() => setHostSearchMode("name")}
                >
                  Noms
                </button>
                <button
                  class={`fb-search-mode-btn${hostSearchMode() === "content" ? " is-active" : ""}`}
                  title="Recherche plein texte (index Windows)"
                  disabled={hostPath() === null}
                  onClick={() => setHostSearchMode("content")}
                >
                  Contenu
                </button>
              </div>
              <input
                class="fb-search"
                placeholder={
                  hostPath() === null
                    ? "Ouvrir un lecteur pour rechercher"
                    : hostSearchMode() === "content"
                      ? "Rechercher dans le contenu (host)…"
                      : "Rechercher par nom (host)…"
                }
                value={hostSearch()}
                disabled={hostPath() === null}
                onInput={(e) => setHostSearch(e.currentTarget.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") void runHostSearch();
                }}
              />
              <button
                class="fb-tb-btn"
                title="Rechercher"
                onClick={() => void runHostSearch()}
                disabled={hostPath() === null}
              >
                🔍
              </button>
            </div>
            <div class="fb-crumbs">
              <For each={hostBreadcrumb()}>
                {(c, i) => (
                  <>
                    <Show when={i() > 0}>
                      <span class="fb-crumb-sep">›</span>
                    </Show>
                    <button class="fb-crumb" onClick={() => navHost(c.path)}>
                      {c.label}
                    </button>
                  </>
                )}
              </For>
              <Show when={hostSearching()}>
                <span class="fb-crumb-sep">·</span>
                <span class="fb-search-tag">résultats de recherche</span>
              </Show>
            </div>
            <Show when={hostSearching() && (hostSearchEngine() !== null || hostSearchWarning())}>
              <div class="fb-search-meta">
                <Show when={hostSearchEngine() !== null}>
                  <span class="fb-search-engine" title="Moteur de recherche utilisé">
                    {hostSearchEngine() === "index" ? "🗂 index Windows" : "📂 parcours fichiers"}
                  </span>
                </Show>
                <Show when={hostSearchWarning()}>
                  <span class="fb-search-warn">⚠ {hostSearchWarning()}</span>
                </Show>
              </div>
            </Show>
            <Show when={hostError()}>
              <div class="fb-error">{hostError()}</div>
            </Show>
            <FileGrid
              side="host"
              entries={hostDisplay}
              loading={hostLoading}
              sort={hostSort}
              onSort={(k) => toggleSort(setHostSort, hostSort, k)}
              selected={hostSelected}
              onSelect={(n, e) => toggleSelect("host", n, e)}
              onOpen={(en) => openHostEntry(en)}
            />
            <div class="fb-pane-actions">
              <button
                class="files-btn"
                disabled={hostSelected().size === 0}
                onClick={() => void downloadSelectionToLocal()}
              >
                ← Télécharger
              </button>
            </div>
          </div>
        </div>

        {/* ── BARRE DE TRANSFERTS ──────────────────────────────────────── */}
        <div class="fb-transfers">
          <div class="fb-transfers-head">
            <span>Transferts</span>
            <span class="files-col-count">{txCount()}</span>
          </div>
          <div class="fb-transfers-body">
            <Show when={txCount() === 0}>
              <div class="fb-empty">Aucun transfert. Glissez un fichier d'un pane à l'autre.</div>
            </Show>

            <For each={uploads()}>
              {(up) => {
                const b = statusBadge(up.status);
                return (
                  <div class="files-card">
                    <div class="files-card-top">
                      <span class="files-card-icon">⬆ {fileIcon(up.name)}</span>
                      <div class="files-card-meta">
                        <div class="files-card-name" title={up.hostPath ? `${up.name} → ${up.hostPath}` : up.name}>
                          {up.name}
                          <Show when={up.hostPath}>
                            <span class="fb-dest"> → {up.hostPath}</span>
                          </Show>
                        </div>
                        <div class="files-card-sub">
                          <span>{fmtBytes(up.size)}</span>
                          <span class={`files-badge ${b.cls}`}>{b.label}</span>
                          <Show when={up.status === "active"}>
                            <span>
                              {pct(up.sent, up.size)}% · {fmtRate(up.rate)}
                            </span>
                          </Show>
                          <Show when={up.status === "done" && up.integrityOk === true}>
                            <span class="files-integrity-ok">✓ sha256</span>
                          </Show>
                          <Show when={up.integrityOk === false}>
                            <span class="files-integrity-bad">✗ intégrité</span>
                          </Show>
                          <Show when={up.error}>
                            <span class="files-integrity-bad">{up.error}</span>
                          </Show>
                        </div>
                      </div>
                      <div class="files-card-actions">
                        <Show when={up.needsHandle && !up.file}>
                          <button class="files-btn files-btn--primary" onClick={() => void reselectUpload(up)}>
                            Re-sélectionner
                          </button>
                        </Show>
                        <Show when={(up.status === "active" || up.status === "paused") && up.file}>
                          <button class="files-btn" onClick={() => togglePause(up)}>
                            {up.paused ? "Reprendre" : "Pause"}
                          </button>
                          <button class="files-btn files-btn--danger" onClick={() => cancelUpload(up)}>
                            Annuler
                          </button>
                        </Show>
                        <Show when={up.status === "done" || up.status === "error" || up.status === "cancelled"}>
                          <button class="files-btn" onClick={() => removeUpload(up)}>
                            ✕
                          </button>
                        </Show>
                      </div>
                    </div>
                    <div class="files-progress">
                      <div class={fillCls(up.status)} style={{ width: `${pct(up.sent, up.size)}%` }} />
                    </div>
                  </div>
                );
              }}
            </For>

            <For each={downloads()}>
              {(dl) => {
                const b = statusBadge(dl.status);
                return (
                  <div class="files-card">
                    <div class="files-card-top">
                      <span class="files-card-icon">⬇ {fileIcon(dl.name)}</span>
                      <div class="files-card-meta">
                        <div class="files-card-name" title={dl.hostPath ? `${dl.hostPath} → local` : dl.name}>
                          {dl.name}
                          <Show when={dl.hostPath}>
                            <span class="fb-dest"> ← {dl.hostPath}</span>
                          </Show>
                        </div>
                        <div class="files-card-sub">
                          <span>{fmtBytes(dl.size)}</span>
                          <span class={`files-badge ${b.cls}`}>{b.label}</span>
                          <Show when={dl.status === "active"}>
                            <span>
                              {pct(dl.received, dl.size)}% · {fmtRate(dl.rate)}
                            </span>
                          </Show>
                          <Show when={dl.status === "done" && dl.integrityOk === true}>
                            <span class="files-integrity-ok">✓ sha256</span>
                          </Show>
                          <Show when={dl.integrityOk === false}>
                            <span class="files-integrity-bad">✗ intégrité</span>
                          </Show>
                          <Show when={dl.error}>
                            <span class="files-integrity-bad">{dl.error}</span>
                          </Show>
                        </div>
                      </div>
                      <div class="files-card-actions">
                        <Show when={dl.status === "paused"}>
                          <button class="files-btn files-btn--primary" onClick={() => resumeDownload(dl)}>
                            Reprendre
                          </button>
                        </Show>
                        <Show when={dl.status === "done" && dl.objectUrl}>
                          <a class="files-btn files-btn--primary" href={dl.objectUrl!} download={dl.name}>
                            Télécharger
                          </a>
                        </Show>
                        <button class="files-btn" onClick={() => removeDownload(dl)}>
                          ✕
                        </button>
                      </div>
                    </div>
                    <div class="files-progress">
                      <div class={fillCls(dl.status)} style={{ width: `${pct(dl.received, dl.size)}%` }} />
                    </div>
                  </div>
                );
              }}
            </For>
          </div>
        </div>
      </div>
    </PanelLayout>
  );
};

// Auto-enregistrement dans le registre des panneaux (cf. import.meta.glob).
registerPanel("files", FilesPanel);

export default FilesPanel;
